import * as React from 'react';
import { useEffect, useState } from 'react';

import { useCollection } from '@amzn/awsui-collection-hooks';

import {
  Button,
  CollectionPreferences,
  CollectionPreferencesProps,
  ColumnLayout,
  Container,
  Header,
  KeyValuePairs,
  Pagination,
  Table,
  TextContent,
  TextFilter,
} from '@amzn/awsui-components-react-v3';
import { Link } from 'src/components/common/Link';
import { StatusIcon } from './statusIcon';
import { defaultWrapLinesPreference, paginationLabels } from 'src/commons/tables';
import { DATA_PERMISSION_LAKE_FORMATION_TYPE } from 'src/commons/constants';
import { createRequestContextDetailLink, createRequestDetailsLink } from 'src/routes';
import { getCsvDataForDataPermission } from 'src/commons/common';
import CsvDownloaderWrapper, { generateCsvColumnDefinitions } from 'src/components/common/csvDownloaderWrapper';
import { PageHeader } from 'src/components/common/PageHeader';
import { EmptyState } from 'src/commons/EmptyState';
import { getRequestContextIdsForRequestId } from 'src/components/workspaces/common/common';

export const typeToHumanReadableObject = {
  LF: 'Lake Formation',
  IAM: 'IAM',
  'LF-EMR': 'Lake Formation - EMR',
  S3: 'S3',
  REDSHIFT: 'Redshift',
  glueLF: 'Lake Formation',
  redshift: 'Redshift',
  redshiftDZ: 'Redshift',
  glue: 'IAM',
};

export const FilterRamResource = ({ resource }) => {
  if (resource.type === 'glue:Table') return resource.arn.split('table/')[1];
  if (resource.type === 'glue:Database') return resource.arn.split('/')[1];
  if (resource.type === 'glue:Catalog') return resource.arn.split(':')[4];
};

export const DataPermissionInformation = ({ dataPermissionItem }) => {
  const [requestContextIds, setRequestContextIds] = useState<string[]>([]);

  useEffect(() => {
    getRequestContextDetails();
  }, [dataPermissionItem]);

  const getRequestContextDetails = async () => {
    if (!!dataPermissionItem.permissionRequestId) {
      const rcIds = await getRequestContextIdsForRequestId(dataPermissionItem.permissionRequestId);
      setRequestContextIds(rcIds);
    }
  };

  return (
    <Container header={<Header variant={'h2'}>Data permission details</Header>}>
      <KeyValuePairs
        columns={4}
        items={[
          { label: 'Owner ID', value: dataPermissionItem.ownerId },
          { label: 'Source account', value: dataPermissionItem.catalogId },
          { label: 'Source database', value: dataPermissionItem.databaseName },
          { label: 'Source table', value: dataPermissionItem.tableName },
          { label: 'Data lake principal', value: dataPermissionItem.dataLakePrincipal },
          { label: 'Share type', value: dataPermissionItem.type },
          {
            label: 'Status',
            value: (
              <div>
                {dataPermissionItem.status && <StatusIcon status={dataPermissionItem.status} />}
                {dataPermissionItem.dateActive && <div>Date: {dataPermissionItem.dateActive}</div>}
                {dataPermissionItem.dateInActive !== undefined && dataPermissionItem.dateInActive !== null && (
                  <div>Date inactive: {dataPermissionItem.dateInActive}</div>
                )}
                {dataPermissionItem.permissionRequestId !== undefined &&
                  dataPermissionItem.permissionRequestId !== null && (
                    <div>
                      Request: <br />
                      <Link href={createRequestDetailsLink(dataPermissionItem.permissionRequestId)}>
                        {dataPermissionItem.permissionRequestId}
                      </Link>
                    </div>
                  )}
              </div>
            ),
          },
          requestContextIds?.length > 0
            ? {
                label: "Request context ID's",
                value: (
                  <TextContent>
                    <ul>
                      {requestContextIds.map((requestContextId) => (
                        <li>
                          <Link href={createRequestContextDetailLink(requestContextId)}>{requestContextId}</Link>{' '}
                        </li>
                      ))}
                    </ul>
                  </TextContent>
                ),
              }
            : undefined,
          {
            label: 'Audit status',
            value: (
              <div>
                {dataPermissionItem.auditStatus && <StatusIcon status={dataPermissionItem.auditStatus} />}
                {dataPermissionItem.dateOfLastAudit && <div>Date: {dataPermissionItem.dateOfLastAudit}</div>}
              </div>
            ),
          },
        ].filter((item) => item != undefined)}
      />
    </Container>
  );
};

export const DataPermissionLakeFormation = ({ dataPermissionItem }) => (
  <Container header={<Header variant={'h2'}>Lake Formation permissions</Header>}>
    <KeyValuePairs
      columns={2}
      items={[
        { label: 'Principal', value: dataPermissionItem.dataLakePrincipal },
        {
          label: 'Principal type',
          value: /^\d+$/.test(dataPermissionItem.dataLakePrincipal) ? 'AWS account' : 'IAM role',
        },
        { label: 'Resource', value: 'Column' } /*We mostly only support column access we can revisit to change this*/,
        {
          label: 'Resource name',
          value: (
            <div>
              <div>Database: {dataPermissionItem.databaseName}</div>
              <div>Table: {dataPermissionItem.tableName}</div>
              <div>
                Included columns:{' '}
                {dataPermissionItem.columns !== undefined && dataPermissionItem.columns !== null
                  ? dataPermissionItem.columns.join(', ')
                  : 'All'}
              </div>
            </div>
          ),
        },
        dataPermissionItem.type === DATA_PERMISSION_LAKE_FORMATION_TYPE
          ? {
              label: 'Permissions',
              value:
                dataPermissionItem.audit !== undefined
                  ? JSON.parse(dataPermissionItem.audit.PermissionsString).join(', ')
                  : '',
            }
          : undefined,
        dataPermissionItem.type === DATA_PERMISSION_LAKE_FORMATION_TYPE
          ? {
              label: 'Grantable permissions',
              value:
                dataPermissionItem.audit !== undefined
                  ? JSON.parse(dataPermissionItem.audit.PermissionsWithGrantOptionString).join(', ')
                  : '',
            }
          : undefined,
      ].filter((item) => item != undefined)}
    />
  </Container>
);

export const DataPermissionRedshiftAdhocUserInformation = ({ dataPermissionItem }) => {
  const [requestContextIds, setRequestContextIds] = useState<string[]>([]);

  useEffect(() => {
    getRequestContextDetails();
  }, [dataPermissionItem]);

  const getRequestContextDetails = async () => {
    if (!!dataPermissionItem.permissionRequestId) {
      const rcIds = await getRequestContextIdsForRequestId(dataPermissionItem.permissionRequestId);
      setRequestContextIds(rcIds);
    }
  };

  return (
    <Container header={<Header variant={'h2'}>Data permission details</Header>}>
      <KeyValuePairs
        columns={4}
        items={[
          { label: 'Owner ID', value: dataPermissionItem.ownerId },
          { label: 'Redshift cluster ID', value: dataPermissionItem.audit['REDSHIFT_CLUSTER_ID'] },
          { label: 'Redshift group', value: dataPermissionItem.audit['REDSHIFT_GROUP'] },
          { label: 'Redshift role', value: dataPermissionItem.audit['REDSHIFT_ROLE'] },
          { label: 'Redshift user', value: dataPermissionItem.audit['REDSHIFT_USER'] },
          { label: 'Consumer principal', value: dataPermissionItem.consumerPrincipal },
          { label: 'Share type', value: dataPermissionItem.type },
          {
            label: 'Status',
            value: (
              <div>
                {dataPermissionItem.status && <StatusIcon status={dataPermissionItem.status} />}
                {dataPermissionItem.dateActive && <div>Date: {dataPermissionItem.dateActive}</div>}
                {dataPermissionItem.dateInActive !== undefined && dataPermissionItem.dateInActive !== null && (
                  <div>Date inactive: {dataPermissionItem.dateInActive}</div>
                )}
                {dataPermissionItem.permissionRequestId !== undefined &&
                  dataPermissionItem.permissionRequestId !== null && (
                    <div>
                      Request: <br />
                      <Link href={createRequestDetailsLink(dataPermissionItem.permissionRequestId)}>
                        {dataPermissionItem.permissionRequestId}
                      </Link>
                    </div>
                  )}
              </div>
            ),
          },
          requestContextIds?.length > 0
            ? {
                label: "Request context ID's",
                value: (
                  <TextContent>
                    <ul>
                      {requestContextIds.map((requestContextId) => (
                        <li>
                          <Link href={createRequestContextDetailLink(requestContextId)}>{requestContextId}</Link>{' '}
                        </li>
                      ))}
                    </ul>
                  </TextContent>
                ),
              }
            : undefined,
        ].filter((item) => item != undefined)}
      />
    </Container>
  );
};

export const DataPermissionRAMResourceShare = ({ ramResourceShare }) => {
  if (ramResourceShare === undefined) {
    return (
      <Container header={<Header variant={'h2'}>RAM share summary</Header>}>
        <ColumnLayout columns={1} variant='text-grid'>
          <div data-awsui-column-layout-root='true'>
            <div>
              <div>No Data Stored. Run Audit.</div>
            </div>
          </div>
        </ColumnLayout>
      </Container>
    );
  }
  return (
    <Container header={<Header variant={'h2'}>RAM share summary</Header>}>
      <KeyValuePairs
        columns={4}
        items={[
          { label: 'Name', value: ramResourceShare.name },
          { label: 'Owner', value: ramResourceShare.owningAccountId },
          {
            label: 'Created on',
            value: new Date(ramResourceShare.creationTime)
              .toISOString()
              .replace('-', '/')
              .split('T')[0]
              .replace('-', '/'),
          },
          { label: 'Status', value: <StatusIcon status={ramResourceShare.status} /> },
          { label: 'ID', value: ramResourceShare.resourceShareArn.split('/')[1] },
          { label: 'ARN', value: ramResourceShare.resourceShareArn },
          {
            label: 'Allow external principals',
            value: ramResourceShare.allowExternalPrincipals === true ? 'Yes' : 'No',
          },
        ].filter((item) => item != undefined)}
      />
    </Container>
  );
};

export const DataPermissionConsumers = ({ dataPermissions, columnDefinitions, dataPermissionsLoading }) => {
  const [csvColumnDefinition, setCsvColumnDefinition] = useState([]);
  const [csvData, setCsvData] = useState([]);
  const [preferences, setPreferences] = useState<CollectionPreferencesProps.Preferences>({
    wrapLines: false,
    pageSize: 10,
  });

  const refForCSVDownloader: React.RefObject<any> = React.createRef();
  const onClickDownload = () => {
    refForCSVDownloader.current.handleClick();
  };
  const generateCsvFileName = () => {
    // eg: consumers-2024-09-13.csv
    return `consumers-${new Date().toISOString().split('T')[0]}`;
  };

  useEffect(() => {
    if (dataPermissions) {
      let csvData = dataPermissions.map((item) => getCsvDataForDataPermission(item));
      setCsvData(csvData);
      setCsvColumnDefinition(generateCsvColumnDefinitions(csvData));
    }
  }, [dataPermissions, dataPermissionsLoading]);

  const { items, collectionProps, paginationProps, filterProps, filteredItemsCount } = useCollection(dataPermissions, {
    filtering: {
      empty: <EmptyState title={'No dataset permissions'} subtitle={'There are no dataset permissions to display.'} />,
      noMatch: '',
    },
    pagination: { pageSize: preferences.pageSize },
    sorting: {},
    selection: {},
    propertyFiltering: {
      filteringProperties: [],
    },
  });

  return (
    <Table
      {...collectionProps}
      loadingText='Loading Consumers...'
      columnDefinitions={columnDefinitions}
      items={items}
      wrapLines={false}
      resizableColumns={true}
      header={
        <PageHeader
          buttons={[]}
          counter={`(${dataPermissions.length})`}
          header={'Consumers'}
          additionalItems={
            !dataPermissionsLoading &&
            dataPermissions.length > 0 && (
              <>
                <Button variant='primary' onClick={onClickDownload} iconName='download'>
                  Download as CSV
                </Button>
                <CsvDownloaderWrapper
                  rowItems={csvData}
                  fileName={generateCsvFileName()}
                  refForCsvDownloader={refForCSVDownloader}
                  userFriendlyDataDefinitions={csvColumnDefinition}
                />
              </>
            )
          }
        />
      }
      loading={dataPermissionsLoading}
      filter={
        <TextFilter
          {...filterProps}
          filteringAriaLabel='Filter resources'
          filteringPlaceholder='Find resources'
          countText={`${filteredItemsCount} ${filteredItemsCount === 1 ? 'match' : 'matches'}`}
        />
      }
      pagination={<Pagination {...paginationProps} ariaLabels={paginationLabels} />}
      preferences={
        <CollectionPreferences
          title={'Preferences'}
          confirmLabel={'Confirm'}
          cancelLabel={'Cancel'}
          preferences={preferences}
          onConfirm={({ detail }) => setPreferences(detail)}
          pageSizePreference={{
            title: 'Page size',
            options: [
              { value: 10, label: '10 items' },
              { value: 20, label: '20 items' },
              { value: 50, label: '50 items' },
            ],
          }}
          wrapLinesPreference={defaultWrapLinesPreference}
        />
      }
    />
  );
};

export const DataPermissionRAMResources = ({ ramResources }) => {
  const [preferences, setPreferences] = useState<CollectionPreferencesProps.Preferences>({
    wrapLines: false,
    pageSize: 10,
  });

  const { items, collectionProps, paginationProps, filterProps, filteredItemsCount } = useCollection(ramResources, {
    filtering: {
      empty: <EmptyState title={'No dataset permissions'} subtitle={'No dataset permissions to display.'} />,
      noMatch: '',
    },
    pagination: { pageSize: preferences.pageSize },
    sorting: {},
    selection: {},
    propertyFiltering: {
      filteringProperties: [],
    },
  });

  return (
    <Table
      {...collectionProps}
      loadingText='Loading Resources...'
      columnDefinitions={[
        {
          id: 'resourceId',
          header: 'Resource ID',
          cell: (item) => <FilterRamResource resource={item} />,
          minWidth: 200,
        },
        {
          id: 'type',
          header: 'Resource type',
          cell: (item) => item['type'],
          minWidth: 120,
        },
        {
          id: 'status',
          header: 'Status',
          cell: (item) => <StatusIcon status={item['status']} />,
          minWidth: 120,
        },
      ]}
      items={items}
      wrapLines={false}
      resizableColumns={true}
      header={
        <Header variant={'h2'} counter={`(${ramResources.length})`}>
          RAM shared resources
        </Header>
      }
      empty={
        <EmptyState
          title={'No resources'}
          subtitle={
            'No resources to display. Try running an audit. If there are still no resources, check the RAM console and re-request.'
          }
        />
      }
      filter={
        <TextFilter
          {...filterProps}
          filteringAriaLabel='Filter resources'
          filteringPlaceholder='Find resources'
          countText={`${filteredItemsCount} ${filteredItemsCount === 1 ? 'match' : 'matches'}`}
        />
      }
      pagination={<Pagination {...paginationProps} ariaLabels={paginationLabels} />}
      preferences={
        <CollectionPreferences
          title={'Preferences'}
          confirmLabel={'Confirm'}
          cancelLabel={'Cancel'}
          preferences={preferences}
          onConfirm={({ detail }) => setPreferences(detail)}
          pageSizePreference={{
            title: 'Page size',
            options: [
              { value: 5, label: '5 items' },
              { value: 10, label: '10 items' },
              { value: 20, label: '20 items' },
            ],
          }}
          wrapLinesPreference={defaultWrapLinesPreference}
          visibleContentPreference={{
            title: 'Visible content',
            options: [
              {
                label: 'Columns',
                options: [
                  {
                    id: 'resourceId',
                    label: 'Resource ID',
                    editable: false,
                  },
                  {
                    id: 'type',
                    label: 'Resource type',
                    editable: true,
                  },
                  {
                    id: 'status',
                    label: 'Status',
                    editable: true,
                  },
                ],
              },
            ],
          }}
        />
      }
    />
  );
};
